import { useRef, useState } from "react";
import { ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { useApi, errMsg, useFileUrl } from "@/lib/api";

export const MAX_JOB_PHOTOS = 5;

export function FileImage({ path, alt = "", className = "", testId }) {
  const url = useFileUrl(path);
  return url ? <img src={url} alt={alt} className={className} data-testid={testId} loading="lazy" />
    : <div className={`bg-slate-100 animate-pulse ${className}`} data-testid={testId} />;
}

export function JobPhotosInput({ value, onChange }) {
  const api = useApi();
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const upload = async (files) => {
    const room = MAX_JOB_PHOTOS - value.length;
    const picked = [...files].slice(0, room);
    if (files.length > room) toast.error(`Max ${MAX_JOB_PHOTOS} photos`);
    setBusy(true);
    const added = [];
    for (const f of picked) {
      const fd = new FormData();
      fd.append("file", f);
      try { added.push((await api("post", "/jobs/photos", fd)).path); } catch (e) { toast.error(errMsg(e)); }
    }
    onChange([...value, ...added]);
    setBusy(false);
    ref.current.value = "";
  };
  return (
    <div className="flex flex-wrap gap-3" data-testid="job-photos-input">
      {value.map((p, i) => (
        <div key={p} className="relative w-28 h-20 rounded-md overflow-hidden border border-slate-200">
          <FileImage path={p} className="w-full h-full object-cover" testId={`job-photo-thumb-${i}`} />
          {i === 0 && <span className="absolute bottom-1 left-1 bg-slate-900/80 text-white text-[10px] px-1.5 rounded">Cover</span>}
          <button type="button" onClick={() => onChange(value.filter((x) => x !== p))} data-testid={`job-photo-remove-${i}`}
            className="absolute top-1 right-1 bg-white/90 rounded-full p-0.5 hover:bg-white"><X size={14} /></button>
        </div>
      ))}
      {value.length < MAX_JOB_PHOTOS && (
        <button type="button" disabled={busy} onClick={() => ref.current.click()} data-testid="job-photo-add-button"
          className="w-28 h-20 rounded-md border-2 border-dashed border-slate-300 text-slate-500 hover:border-sky-400 hover:text-sky-700 flex flex-col items-center justify-center text-xs gap-1 transition-colors">
          <ImagePlus size={18} />{busy ? "Uploading..." : `Add photo (${value.length}/${MAX_JOB_PHOTOS})`}
        </button>
      )}
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" multiple hidden onChange={(e) => upload(e.target.files)} data-testid="job-photo-file-input" />
    </div>
  );
}

export function JobGallery({ photos }) {
  const [active, setActive] = useState(0);
  if (!photos?.length) return null;
  return (
    <div className="mt-8" data-testid="job-gallery">
      <FileImage path={photos[active]} className="w-full aspect-[16/9] max-h-[420px] object-cover rounded-lg" testId="job-gallery-main" />
      {photos.length > 1 && (
        <div className="mt-3 flex gap-2 overflow-x-auto">
          {photos.map((p, i) => (
            <button key={p} onClick={() => setActive(i)} data-testid={`job-gallery-thumb-${i}`}
              className={`shrink-0 w-24 h-16 rounded-md overflow-hidden border-2 transition-colors ${i === active ? "border-sky-500" : "border-transparent opacity-70 hover:opacity-100"}`}>
              <FileImage path={p} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
